import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HandHelping, Gift, Handshake, Building, Heart, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getInvolvedOptions } from '../data/mock';

const GetInvolvedPage = () => {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [volunteerForm, setVolunteerForm] = useState({
    name: '',
    email: '',
    phone: '',
    interests: '',
    message: ''
  });

  const iconMap = {
    HandHelping: HandHelping,
    Gift: Gift,
    Handshake: Handshake,
    Building: Building
  };

  const donationAmounts = [25, 50, 100, 250, 500, 1000];

  const donationImpact = [
    { amount: '$25', impact: 'Provides educational materials for one family' },
    { amount: '$50', impact: 'Supports one patient at a workshop' },
    { amount: '$100', impact: 'Funds transportation to appointments' },
    { amount: '$250', impact: 'Sponsors a caregiver support session' },
    { amount: '$500', impact: 'Provides a month of nutritional support' },
    { amount: '$1,000', impact: 'Sponsors a family for one quarter' }
  ];

  const handleVolunteerSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    toast.success('Thank you for your interest! We\'ll be in touch soon.');
    setVolunteerForm({
      name: '',
      email: '',
      phone: '',
      interests: '',
      message: ''
    });
  };

  const handleDonate = () => {
    const amount = selectedAmount || customAmount;
    if (amount) {
      toast.success(`Thank you for your ${amount.toLocaleString ? '$' + amount.toLocaleString() : amount} donation!`);
    } else {
      toast.error('Please select or enter a donation amount');
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#5D4E6D] py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E07A5F]/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Get Involved</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 leading-tight">
              Make a Difference Today
            </h1>
            <p className="text-gray-200 text-lg leading-relaxed">
              Join our ohana and help support Hawaii's cancer community. Whether you donate, 
              volunteer, or partner with us, your contribution makes a lasting impact.
            </p>
          </div>
        </div>
      </section>

      {/* Ways to Help */}
      <section className="py-24 bg-[#F5F0EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Ways to Help</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2A32] mt-4">
              How You Can Contribute
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {getInvolvedOptions.map((option) => {
              const IconComponent = iconMap[option.icon] || Heart;
              return (
                <Card
                  key={option.id}
                  className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-[#E07A5F]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-[#E07A5F] transition-colors duration-300">
                      <IconComponent className="w-8 h-8 text-[#E07A5F] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-[#2D2A32] mb-3">{option.title}</h3>
                    <p className="text-gray-600 text-sm mb-6">{option.description}</p>
                    <Button
                      variant="outline"
                      className="border-2 border-[#5D4E6D] text-[#5D4E6D] hover:bg-[#5D4E6D] hover:text-white rounded-full px-6 transition-all duration-300"
                    >
                      {option.cta}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Donation Section */}
      <section id="donate" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Donation Form */}
            <div>
              <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Donate</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2D2A32] mt-4 mb-6">
                Support Our Mission
              </h2>
              <p className="text-gray-600 mb-8">
                Your donation helps us provide resources, support families, and fund programs 
                that make a real difference in the lives of cancer patients and caregivers.
              </p>

              <div className="grid grid-cols-3 gap-4 mb-6">
                {donationAmounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount('');
                    }}
                    className={`py-4 px-6 rounded-xl font-semibold transition-all duration-300 ${
                      selectedAmount === amount
                        ? 'bg-[#E07A5F] text-white shadow-lg'
                        : 'bg-[#F5F0EB] text-[#2D2A32] hover:bg-[#E07A5F]/10'
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>

              <div className="mb-8">
                <Label htmlFor="customAmount" className="text-gray-700">Custom Amount</Label>
                <div className="relative mt-2">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="customAmount"
                    type="number"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                    }}
                    className="pl-8 rounded-xl border-gray-200 focus:border-[#5D4E6D] focus:ring-[#5D4E6D]"
                  />
                </div>
              </div>

              <Button
                onClick={handleDonate}
                size="lg"
                className="w-full bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full py-6 text-lg transition-all duration-300"
              >
                <Heart className="mr-2 w-5 h-5" />
                Donate Now
              </Button>

              <p className="text-gray-500 text-sm text-center mt-4">
                Secure donation processing. Your contribution is tax-deductible.
              </p>
            </div>

            {/* Donation Impact */}
            <div>
              <Card className="border-0 shadow-xl bg-[#5D4E6D] text-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6">Your Impact</h3>
                  <div className="space-y-4">
                    {donationImpact.map((item) => (
                      <div key={item.amount} className="flex items-start">
                        <div className="w-8 h-8 bg-[#E07A5F] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <span className="font-bold text-[#E07A5F]">{item.amount}</span>
                          <p className="text-gray-300">{item.impact}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Volunteer Section */}
      <section id="volunteer" className="py-24 bg-[#F5F0EB]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#E07A5F] font-semibold text-sm uppercase tracking-wider">Volunteer</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2D2A32] mt-4 mb-4">
              Join Our Volunteer Team
            </h2>
            <p className="text-gray-600">
              Make a direct impact in your community by volunteering with us.
            </p>
          </div>

          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleVolunteerSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={volunteerForm.name}
                      onChange={(e) => setVolunteerForm({ ...volunteerForm, name: e.target.value })}
                      className="mt-2 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={volunteerForm.email}
                      onChange={(e) => setVolunteerForm({ ...volunteerForm, email: e.target.value })}
                      className="mt-2 rounded-xl"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={volunteerForm.phone}
                    onChange={(e) => setVolunteerForm({ ...volunteerForm, phone: e.target.value })}
                    className="mt-2 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="interests">Areas of Interest</Label>
                  <Input
                    id="interests"
                    placeholder="e.g., Events, Administrative, Outreach"
                    value={volunteerForm.interests}
                    onChange={(e) => setVolunteerForm({ ...volunteerForm, interests: e.target.value })}
                    className="mt-2 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="message">Why do you want to volunteer?</Label>
                  <Textarea
                    id="message"
                    rows={4}
                    value={volunteerForm.message}
                    onChange={(e) => setVolunteerForm({ ...volunteerForm, message: e.target.value })}
                    className="mt-2 rounded-xl resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-[#5D4E6D] hover:bg-[#4A3D58] text-white rounded-full py-6 transition-all duration-300"
                >
                  Submit Application
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Partners CTA */}
      <section className="py-24 bg-[#2D2A32]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Interested in Corporate Partnership?
          </h2>
          <p className="text-gray-300 text-lg mb-10">
            Partner with us to make a bigger impact. Contact us to learn about sponsorship 
            opportunities and corporate giving programs.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#E07A5F] hover:bg-[#C96A50] text-white rounded-full px-10 transition-all duration-300"
          >
            <Link to="/contact">
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default GetInvolvedPage;
